import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Trash2, AlertTriangle, MessageCircle, BookOpen, ArrowLeft, VolumeX, Volume2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import UserAvatar from '../components/avatars/UserAvatar';
import BookwormCard from '../components/avatars/BookwormCard';
import TranslateButton from '../components/TranslateButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getCoverUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${window.location.origin}${url}`;
  return url;
};

const formatTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Reusable Chat Panel component
const ChatPanel = ({ room, roomName, bookCover = null }) => {
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userRole = localStorage.getItem('userRole');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const lastTimestamp = useRef(null);
  const pollRef = useRef(null);
  const [showMuteModal, setShowMuteModal] = useState(null);
  const [muteHours, setMuteHours] = useState(24);
  const [atBottom, setAtBottom] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const scrollContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const initialLoadDone = useRef(false);

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, []);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?room=${room}&limit=100`);
      setMessages(res.data.messages || []);
      const msgs = res.data.messages || [];
      if (msgs.length > 0) {
        lastTimestamp.current = msgs[msgs.length - 1].created_at;
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [room]);

  const pollNewMessages = useCallback(async () => {
    if (!lastTimestamp.current) return;
    try {
      const res = await axios.get(`${API}/chat/messages/new?room=${room}&after=${lastTimestamp.current}`);
      const newMsgs = res.data.messages || [];
      if (newMsgs.length > 0) {
        setMessages(prev => [...prev, ...newMsgs]);
        lastTimestamp.current = newMsgs[newMsgs.length - 1].created_at;
        // Only auto-scroll if user is already at the bottom
        if (isAtBottomRef.current) {
          setTimeout(() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      }
    } catch (error) {
      console.warn('[CommunityChat] fetchMessages failed:', error?.message || error);
    }
  }, [room]);

  // @mention autocomplete — names of room participants (+ book authors)
  const [participants, setParticipants] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/chat/participants?room=${room}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setParticipants(res.data?.participants || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  const mentionState = React.useMemo(() => {
    const m = newMessage.match(/@([^@]{0,30})$/);
    if (!m || participants.length === 0) return null;
    const q = m[1].toLowerCase();
    const options = participants
      .filter(p => p.name.toLowerCase().startsWith(q))
      .slice(0, 6);
    return options.length ? { options } : null;
  }, [newMessage, participants]);

  const pickMention = (name) => {
    setNewMessage(prev => prev.replace(/@([^@]{0,30})$/, `@${name} `));
  };

  // Highlight @Name tokens in rendered messages
  const renderWithMentions = (text) => {
    if (!text || !text.includes('@')) return text;
    const parts = text.split(/(@[\w'’.-]+(?: [A-Z][\w'’.-]+)?)/g);
    return parts.map((p, i) =>
      p.startsWith('@') ? (
        <span key={i} className="text-amber-300 font-semibold">{p}</span>
      ) : (
        p
      )
    );
  };

  useEffect(() => {
    initialLoadDone.current = false;
    fetchMessages();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchMessages]);

  useEffect(() => {
    if (!loading && !initialLoadDone.current) {
      // Only scroll to bottom on initial load, not on re-renders
      initialLoadDone.current = true;
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 200);
    }
    if (!loading) {
      pollRef.current = setInterval(pollNewMessages, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [loading, pollNewMessages]);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAtBottom(bottom);
    setAtTop(el.scrollTop < 30);
    isAtBottomRef.current = bottom;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/messages`, {
        room, content: newMessage.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });

      setMessages(prev => [...prev, res.data]);
      lastTimestamp.current = res.data.created_at;
      setNewMessage('');
      // Always scroll to bottom after sending your own message
      isAtBottomRef.current = true;
      setAtBottom(true);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 100);

      if (res.data.warning) {
        toast({ title: 'Warning', description: res.data.warning, variant: 'destructive' });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (msgId) => {
    try {
      await axios.delete(`${API}/chat/messages/${msgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.map(m =>
        m.id === msgId ? { ...m, deleted: true, content: '[Message deleted]' } : m
      ));
    } catch {
      toast({ title: 'Error', description: 'Failed to delete message', variant: 'destructive' });
    }
  };

  const handleMute = async (muteUserId) => {
    try {
      await axios.post(`${API}/chat/mute/${muteUserId}`, {
        reason: 'Admin moderation', duration_hours: muteHours
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'User muted', description: `Muted for ${muteHours} hours` });
      setShowMuteModal(null);
    } catch {
      toast({ title: 'Error', description: 'Failed to mute user', variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid={`chat-panel-${room}`}>
      {/* Messages Area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-1"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-red-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageCircle className="h-12 w-12 mb-3 opacity-40" />
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwn = msg.user_id === userId;
            const prevMsg = messages[idx - 1];
            const showHeader = !prevMsg || prevMsg.user_id !== msg.user_id ||
              (new Date(msg.created_at) - new Date(prevMsg.created_at)) > 300000;

            return (
              <div key={msg.id} className={`group ${showHeader ? 'mt-3' : 'mt-0.5'}`}>
                {showHeader && (
                  <div className="flex items-center gap-2 mb-1">
                    <BookwormCard
                      userId={msg.user_id}
                      name={msg.user_name}
                      avatarId={msg.user_avatar_id}
                      avatarPhotoUrl={msg.user_avatar_photo_url}
                    >
                      <span className="inline-flex items-center gap-2">
                        <UserAvatar
                          name={msg.user_name}
                          avatarId={msg.user_avatar_id}
                          avatarPhotoUrl={msg.user_avatar_photo_url}
                          size="sm"
                          testId={`chat-avatar-${msg.id}`}
                          ringColor={isOwn ? 'ring-red-500/50' : ''}
                        />
                        <span className="text-sm font-medium text-white">{msg.user_name}</span>
                      </span>
                    </BookwormCard>
                    {msg.is_book_author && (
                      <span
                        data-testid={`author-badge-${msg.id}`}
                        className="text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5"
                      >
                        Author
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
                  </div>
                )}
                <div className="flex items-start gap-2 pl-9">
                  <div className={`flex-1 min-w-0 ${msg.deleted ? 'opacity-50' : ''}`}>
                    <p className={`text-sm leading-relaxed break-words ${
                      msg.deleted ? 'text-gray-500 italic' :
                      msg.was_censored ? 'text-orange-300' : 'text-gray-200'
                    }`}>
                      {msg.was_censored && !msg.deleted && (
                        <AlertTriangle className="inline h-3 w-3 text-orange-400 mr-1 -mt-0.5" />
                      )}
                      {msg.deleted ? msg.content : renderWithMentions(msg.content)}
                    </p>
                    {/* Inline "Translate to English" — hidden for deleted/censored */}
                    {!msg.deleted && !msg.was_censored && msg.content && (
                      <TranslateButton text={msg.content} sourceId={msg.id} />
                    )}
                  </div>
                  {/* Actions (visible on hover) */}
                  {!msg.deleted && (
                    <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {(isOwn || userRole === 'admin') && (
                        <button onClick={() => handleDelete(msg.id)}
                          className="text-gray-500 hover:text-red-400 p-1" title="Delete"
                          data-testid={`delete-msg-${msg.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {userRole === 'admin' && !isOwn && (
                        <button onClick={() => setShowMuteModal(msg.user_id)}
                          className="text-gray-500 hover:text-orange-400 p-1" title="Mute user"
                          data-testid={`mute-user-${msg.user_id}`}>
                          <VolumeX className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll Up / Down buttons */}
      <div className="absolute right-4 top-2 bottom-16 flex flex-col justify-between pointer-events-none z-10">
        {!atTop && messages.length > 5 && (
          <button onClick={scrollToTop}
            className="pointer-events-auto bg-gray-800 border border-white/10 text-white rounded-full p-2 shadow-lg hover:bg-gray-700 transition-colors"
            data-testid="scroll-to-top">
            <ChevronUp className="h-4 w-4" />
          </button>
        )}
        <div />
        {!atBottom && messages.length > 5 && (
          <button onClick={scrollToBottom}
            className="pointer-events-auto bg-red-600 text-white rounded-full p-2 shadow-lg hover:bg-red-700 transition-colors"
            data-testid="scroll-to-bottom">
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-white/10">
        <div className="relative">
          {mentionState && (
            <div
              className="absolute bottom-full left-0 mb-2 w-64 max-h-56 overflow-y-auto bg-gray-900 border border-white/15 rounded-lg shadow-2xl z-30"
              data-testid="mention-dropdown"
            >
              {mentionState.options.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => pickMention(p.name)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-white/10 flex items-center justify-between gap-2"
                  data-testid={`mention-option-${p.name}`}
                >
                  <span className="truncate">@{p.name}</span>
                  {p.is_author && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-full px-1.5 py-0.5 shrink-0">
                      Author
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={token ? "Type a message... use @ to mention someone" : "Log in to chat"}
            disabled={!token || sending}
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 pr-14"
            maxLength={1000}
            data-testid="chat-input"
          />
          <Button type="submit" disabled={!token || !newMessage.trim() || sending}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-red-600 hover:bg-red-700 h-8 w-10 p-0"
            data-testid="chat-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {/* Mute Modal */}
      {showMuteModal && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20 rounded-xl">
          <div className="bg-gray-900 rounded-xl p-5 border border-white/10 w-72">
            <h4 className="text-white font-bold mb-3 flex items-center gap-2">
              <VolumeX className="h-5 w-5 text-orange-400" /> Mute User
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-sm">Duration (hours)</label>
                <Input type="number" min="1" max="720" value={muteHours}
                  onChange={(e) => setMuteHours(parseInt(e.target.value) || 24)}
                  className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowMuteModal(null)} className="flex-1 text-gray-400">Cancel</Button>
                <Button onClick={() => handleMute(showMuteModal)} className="flex-1 bg-orange-600 hover:bg-orange-700">Mute</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Community Chat Page
const CommunityChat = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState('general');
  const [activeRoomName, setActiveRoomName] = useState('General Chat');
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    axios.get(`${API}/chat/rooms`).then(res => setRooms(res.data)).catch(() => {});
  }, []);

  const selectRoom = (room) => {
    setActiveRoom(room.id);
    setActiveRoomName(room.name);
  };

  return (
    <div className="h-screen bg-black flex flex-col" data-testid="community-chat-page">
      {/* Top Bar */}
      <div className="bg-gray-950 border-b border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <MessageCircle className="h-5 w-5 text-red-500" />
          <h1 className="text-lg font-bold text-white">Community Chat</h1>
        </div>
        <button onClick={() => setShowSidebar(!showSidebar)}
          className="text-gray-400 hover:text-white text-sm md:hidden">
          {showSidebar ? 'Hide Rooms' : 'Show Rooms'}
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Room Sidebar */}
        <div className={`${showSidebar ? 'w-64' : 'w-0 overflow-hidden'} flex-shrink-0 border-r border-white/10 bg-gray-950/50 flex flex-col transition-all`}>
          <div className="p-3">
            <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-2">Chat Rooms</p>
          </div>
          <div className="flex-1 overflow-y-auto px-2 space-y-1">
            {/* General room */}
            <button
              onClick={() => selectRoom({ id: 'general', name: 'General Chat' })}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${
                activeRoom === 'general' ? 'bg-red-600/20 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
              data-testid="room-general"
            >
              <MessageCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm truncate">General Chat</span>
            </button>

            {/* Book rooms */}
            {rooms.filter(r => r.type === 'book').length > 0 && (
              <p className="text-gray-600 text-xs font-medium uppercase tracking-wider px-3 pt-3 pb-1">Book Discussions</p>
            )}
            {rooms.filter(r => r.type === 'book').map(room => (
              <button
                key={room.id}
                onClick={() => selectRoom(room)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${
                  activeRoom === room.id ? 'bg-red-600/20 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`room-${room.id}`}
              >
                {room.cover_image_url ? (
                  <img src={getCoverThumbUrl(room.cover_image_url)} alt="" className="w-8 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <BookOpen className="h-4 w-4 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm truncate">{room.name}</p>
                  <p className="text-xs text-gray-500 truncate">{room.message_count} messages</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 relative mb-16">
          {/* Room Header */}
          <div className="px-4 py-2.5 border-b border-white/10 bg-gray-950/30 flex-shrink-0">
            <h2 className="text-white font-medium text-sm">{activeRoomName}</h2>
          </div>
          <ChatPanel room={activeRoom} roomName={activeRoomName} />
        </div>
      </div>
    </div>
  );
};

export { ChatPanel };
export default CommunityChat;
